import { useContext, useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import axios from 'axios'

import HomeIcon from '../../../assets/svgs/Home'
import Icon from '../../../assets/svgs/Icon'
import UserIcon from '../../../assets/svgs/User'

import style from './DashHeader.module.css'
import UploadModal from '../files/UploadModal'
import StartYourJourrney from '../../../utils/StartJourney'

import GuestContext from '../../../contexts/GuestContext'
import { useAuthContext } from '../../../contexts/AuthContext'
import { useCV } from '../../../contexts/CVContext'
import useWindowWidth from '../../../hooks/useWindowWidth'

// Only these tabs are enabled
const ALLOWED_TABS = new Set(['Home', 'Profile'])

const DashHeader = ({
  selectedTab,
  handleSelectTab,
  openGuestInfo,
  setOpenGuestInfo,
  openReg,
  setOpenAuth,
  handleAuth,
  user,
}) => {
  const location = useLocation()
  const [openUpload, setOpenUpload] = useState(false)
  const { setGuestUser } = useContext(GuestContext)
  const { isAuthenticated, logout, isSuperUser } = useAuthContext()
  const { isCVUploaded, setIsCVUploaded } = useCV()
  const [loading, setLoading] = useState(false)
  const innerWidth = useWindowWidth()

  const url = process.env.REACT_APP_API_URL || ''

  const [openUserMenu, setOpenUserMenu] = useState(false)
  const userMenuRef = useRef(null)
  const [openMenu, setOpenMenu] = useState(false)

  const navigate = useNavigate()

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setOpenUserMenu(false)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenUserMenu(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    setOpenUserMenu(false)
  }, [location.pathname])

  const goToCVAnalyzer = () => {
    setOpenUserMenu(false)
    navigate('/dashboard/career-companion')
  }

  const ALLOWED_REDIRECT_ORIGINS = ['https://chimpknows.com', 'http://localhost:3000', 'http://localhost:3001']
  const getSafeLogoutUrl = (configuredUrl) => {
    const fallback = 'https://chimpknows.com/'
    if (!configuredUrl) return fallback
    try {
      const parsed = new URL(configuredUrl)
      if (ALLOWED_REDIRECT_ORIGINS.includes(parsed.origin)) return configuredUrl
    } catch { /* invalid URL */ }
    return fallback
  }

  // Logout -> API + clear + external redirect
  const handleLogout = async (e) => {
    e?.preventDefault?.()
    try {
      await axios.post(`${url}/users/logout`, {}, { withCredentials: true })
    } catch (error) {
      console.error('Error logging out:', error?.message || error)
    } finally {
      logout?.()
      window.location.replace(getSafeLogoutUrl(process.env.REACT_APP_MAIN_SITE_URL))
    }
  }

  // My profile
  const goToProfile = () => {
    setOpenUserMenu(false)
    handleSelectTab('Profile')
  }

  // Fetch user's files (for CV flag)
  useEffect(() => {
    const fetchUserFiles = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`${url}/ai/user/get-files`, {
          withCredentials: true,
          headers: { Accept: 'application/json' },
        })
        const { files } = response.data
        const hasCV = files.length > 0
        setIsCVUploaded(hasCV)
        Cookies.set('isCVUploaded', hasCV)
      } catch (error) {
        console.error('Error fetching user files:', error)
      } finally {
        setLoading(false)
      }
    }

    if (isAuthenticated) fetchUserFiles()
  }, [isAuthenticated])

  return (
    <>
      <div
        className={`${style.dashHeader} ${selectedTab === 'Chat' ? style.hidden : ''
          } ${location.pathname === '/dashboard/connect/create-new-event' &&
          style.hiddenHeader
          }`}
      >
        {/* LEFT: Logo */}
        <div className={style.icon} onClick={() => handleSelectTab('Home')}>
          <Icon width="40" height="40" color={'#432C5B'} />
          <span className={style.logoText}>CHIMP</span>
        </div>

        {/* CENTER: Navigation Pills */}
        {innerWidth > 768 && (
          <div className={style.menuItemsContainer}>
            {['Home', 'CV Analyzer', 'Spotlight', 'Connect', 'Roadmaps'].map((tab) => {

              if (tab === 'CV Analyzer') {
                const isActive = location.pathname.includes('career-companion')
                return (
                  <div
                    key={tab}
                    className={`${style.menuItem} ${isActive ? style.highlightedTab : ''}`}
                    onClick={goToCVAnalyzer}
                    data-log-action="nav_cv_analyzer"
                  >
                    CV Analyzer
                  </div>
                )
              }

              let isEnabled = ALLOWED_TABS.has(tab)
              // Spotlight specific check
              if (tab === 'Spotlight') {
                isEnabled = isAuthenticated && isSuperUser
              }
              const isActive = selectedTab === tab
              return (
                <div
                  key={tab}
                  className={`${style.menuItem} ${isActive ? style.highlightedTab : ''} ${!isEnabled ? style.disabled : ''}`}
                  onClick={() => {
                    if (isEnabled) handleSelectTab(tab)
                  }}
                  data-log-action={`nav_${tab.toLowerCase()}`}
                >
                  {tab}
                </div>
              )
            })}
          </div>
        )}

        {/* RIGHT: Actions (Start Journey + User Menu) */}
        <div className={style.rightActions}>
          {innerWidth > 768 && !isCVUploaded && !loading && (
            <StartYourJourrney
              handleAuth={handleAuth}
              openReg={openReg}
              setOpenAuth={setOpenAuth}
              user={user}
            />
          )}

          {!isAuthenticated && innerWidth > 768 && (
            <div className={style.userMenuWrapper}> {/* Wrap buttons for spacing */}
              <div
                className={style.dashButton}
                onClick={() => {
                  setOpenGuestInfo(false)
                  handleAuth(!openReg)
                  setOpenAuth('login')
                }}
                style={{ marginRight: 8, background: 'transparent', color: '#432c5b', boxShadow: 'none' }}
              >
                Log in
              </div>
              <div
                className={style.dashButton}
                onClick={() => {
                  setOpenGuestInfo(false)
                  handleAuth(!openReg)
                  setOpenAuth('register')
                }}
              >
                Sign up
              </div>
            </div>
          )}

          {/* User Profile Dropdown */}
          {isAuthenticated && innerWidth > 768 && (
            <div className={style.userMenuWrapper} ref={userMenuRef}>
              <div
                className={style.iconContainer}
                onClick={() => setOpenUserMenu((v) => !v)}
                role="button"
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
                data-log-action="open_user_menu"
              >
                <UserIcon width={'18'} height={'18'} color={'#ffffff'} />
              </div>

              {openUserMenu && (
                <div className={style.userMenu} role="menu">
                  <button
                    className={style.userMenuItem}
                    onClick={goToProfile}
                    role="menuitem"
                  >
                    <UserIcon width={'16'} height={'16'} color={'#432C5B'} />
                    My profile
                  </button>
                  <button
                    className={style.userMenuItem}
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    {/* Logout Icon */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#432c5b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                    Log out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* MOBILE TOP BAR — hamburger only */}
        <div className={style.mobileMenuContainer}>
          <button
            className={style.hamburger}
            onClick={() => setOpenMenu(true)}
            aria-label="Open navigation"
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Journey banner (unchanged) */}
      {innerWidth < 768 &&
        !location.pathname.startsWith('/dashboard/chat') &&
        !location.pathname.startsWith('/dashboard/connect/create-new-event') &&
        !isCVUploaded &&
        !loading && (
          <StartYourJourrney
            handleAuth={handleAuth}
            openReg={openReg}
            setOpenAuth={setOpenAuth}
          />
        )}

      {/* ── Mobile side drawer ─────────────────────────────── */}
      {openMenu && (
        <div className={style.drawerOverlay} onClick={() => setOpenMenu(false)} aria-hidden="true" />
      )}
      <div className={`${style.drawer} ${openMenu ? style.drawerOpen : ''}`}>
        <div className={style.drawerHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon width="28" height="28" color="#fff" />
            <span className={style.drawerLogoText}>CHIMP</span>
          </div>
          <button className={style.drawerClose} onClick={() => setOpenMenu(false)} aria-label="Close menu">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <nav className={style.drawerNav}>
          <button
            className={`${style.drawerNavItem} ${selectedTab === 'Home' ? style.drawerNavItemActive : ''}`}
            onClick={() => { setOpenMenu(false); handleSelectTab('Home') }}
          >
            <HomeIcon />
            <span>Home</span>
          </button>
          <button
            className={`${style.drawerNavItem} ${location.pathname.includes('career-companion') ? style.drawerNavItemActive : ''}`}
            onClick={() => { setOpenMenu(false); goToCVAnalyzer() }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            <span>CV Analyzer</span>
          </button>

          {/* Map + Chat — only relevant on the Home/Discover tab */}
          {selectedTab === 'Home' && (
            <>
              <div className={style.drawerSectionDivider} />
              <button
                className={style.drawerNavItem}
                onClick={() => { setOpenMenu(false); window.dispatchEvent(new CustomEvent('chimp:toggleMap')) }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="4" r="2"/><circle cx="4" cy="20" r="2"/><circle cx="20" cy="20" r="2"/>
                  <line x1="12" y1="6" x2="4" y2="18"/><line x1="12" y1="6" x2="20" y2="18"/><line x1="6" y1="20" x2="18" y2="20"/>
                </svg>
                <span>Map</span>
              </button>
              <button
                className={style.drawerNavItem}
                onClick={() => { setOpenMenu(false); window.dispatchEvent(new CustomEvent('chimp:toggleChat')) }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <span>Chat</span>
              </button>
            </>
          )}
        </nav>

        <div className={style.drawerDivider} />

        {isAuthenticated ? (
          <div className={style.drawerUser}>
            <button className={style.drawerUserItem} onClick={() => { setOpenMenu(false); goToProfile() }}>
              <UserIcon width="16" height="16" color="#432C5B" />
              <span>My Profile</span>
            </button>
            <button className={`${style.drawerUserItem} ${style.drawerLogout}`} onClick={() => { setOpenMenu(false); handleLogout() }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              <span>Log out</span>
            </button>
          </div>
        ) : (
          <div className={style.drawerUser}>
            <button
              className={style.drawerAuthBtn}
              onClick={() => { setOpenMenu(false); setOpenGuestInfo(false); handleAuth(!openReg); setOpenAuth('login') }}
            >
              Log in
            </button>
            <button
              className={`${style.drawerAuthBtn} ${style.drawerAuthBtnPrimary}`}
              onClick={() => { setOpenMenu(false); setOpenGuestInfo(false); handleAuth(!openReg); setOpenAuth('register') }}
            >
              Sign up
            </button>
          </div>
        )}
      </div>

      {openUpload && (
        <div className="cover">
          <UploadModal openUpload={openUpload} setOpenUpload={setOpenUpload} />
        </div>
      )}
    </>
  )
}

export default DashHeader
